<!-- PatientBasicInfoToolTip.vue -->
<script setup lang="ts">
    import '../../../../css/choice.css';
    import { computed } from 'vue';
    import { PatientHeaderBasicInfoModel } from '../../../lib/Api/Patient';
    import { mmlConnectionDesc } from './MML'    
    import { patientSetupScreenUrl } from '../../../lib/OfficeMdUrls'
    import { DatabaseIdTypes, MdrCcmStatusType, MdrRpmStatusType } from '../../../Enums/ChoiceEnums';
    import Phone from '../../Formatting/Phone.vue';
    import ToolTipGridContainer from '../../Table/ToolTipGridContainer.vue';
    import PatientHeaderToolTipItem from './PatientHeaderToolTipItem.vue';
    import XSpan from '../../HtmlWrapper/XSpan.vue';
    import Icon from '../../Icon.vue';
    import MMLIcon from '../../Choice/Patient/MMLIcon.vue';
    import { IconTypes } from '../../../Enums/UiEnums';

    const props = defineProps<{
        basicInfo?: PatientHeaderBasicInfoModel
    }>();

    
    const emit = defineEmits<{
            clickMML: [patientEid: number],
            clickEditFlagSetting: []
        }>();
        
    const basicInfo = computed(() => props.basicInfo);

    function onClickMML()
    {
        if (props.basicInfo?.patient_eid)
            emit("clickMML", props.basicInfo?.patient_eid);
    }

    function onClickChartNumber()
    {
        if (props.basicInfo?.patient_eid)
        {
            // TODO: See if this is acceptable
            window.open(patientSetupScreenUrl(DatabaseIdTypes.entity_id, props.basicInfo?.patient_eid));
        }
    }

    const showCcmStatus = computed(() => props.basicInfo?.mdrCcmDate?.length && MdrCcmStatusType.Enrolled === props.basicInfo?.mdrCcmStatus);
    const showRpmStatus = computed(() => props.basicInfo?.mdrRpmDate?.length && MdrRpmStatusType.Enrolled === props.basicInfo?.mdrRpmStatus);

</script>

<style scoped>
    @import url('../../../../css/toolTipGrid.css');
</style>

<template>
    <div v-if="basicInfo">
        <ToolTipGridContainer style="font-size:small">

            <template v-if="basicInfo.group_notes">
                <PatientHeaderToolTipItem>
                    <template #title>
                        Group
                    </template>
                    {{ basicInfo.displayName }}
                </PatientHeaderToolTipItem>
    
                <PatientHeaderToolTipItem>
                    <template #title>
                        Group #
                    </template>
                    {{ basicInfo.patient_id }}
                </PatientHeaderToolTipItem>                
            </template>


            <template v-if="!basicInfo.group_notes">
                <PatientHeaderToolTipItem>
                    <template #title>
                        Patient
                    </template>
                    {{ basicInfo.displayName }}
                </PatientHeaderToolTipItem>
        
                <PatientHeaderToolTipItem>
                    <template #title>
                        Born
                    </template>
                    {{ basicInfo.birthDay }} ({{ basicInfo.age }})
                </PatientHeaderToolTipItem>
        
                <PatientHeaderToolTipItem>
                    <template #title>
                        Gender
                    </template>
                    {{ basicInfo.gender }}
                </PatientHeaderToolTipItem>
        
                <PatientHeaderToolTipItem>
                    <template #title>
                        Primary Ins.
                    </template>
                    {{ basicInfo.patientInsuranceNamePrimary }}
                </PatientHeaderToolTipItem>
        
                <PatientHeaderToolTipItem>
                    <template #title>
                        Secondary Ins.
                    </template>
                    {{ basicInfo.patientInsuranceNameSecondary}}
                </PatientHeaderToolTipItem>

                <PatientHeaderToolTipItem v-if="basicInfo.homePhonePrimary">
                    <template #title>
                        Home
                    </template>
                    <Phone :phone="basicInfo.homePhonePrimary" />
                </PatientHeaderToolTipItem>  
        
                <PatientHeaderToolTipItem v-if="basicInfo.businessPhonePrimary">
                    <template #title>
                        Work
                    </template>
                    <Phone :phone="basicInfo.businessPhonePrimary" />
                </PatientHeaderToolTipItem>                    
        
                <PatientHeaderToolTipItem v-if="basicInfo.otherPhone">
                    <template #title>
                        Other
                    </template>
                    <Phone :phone="basicInfo.otherPhone" />
                </PatientHeaderToolTipItem>                
        
                <template v-if="basicInfo.referringEntityName">
                    <PatientHeaderToolTipItem :topBorder="true">
                        <template #title>
                            Referring
                        </template>
                        {{ basicInfo.referringEntityName}}
                    </PatientHeaderToolTipItem>            
        
                    <PatientHeaderToolTipItem>
                        <template #title>
                            Phone
                        </template>
                        <Phone :phone="basicInfo.referringEntityPhone" />
                    </PatientHeaderToolTipItem>
                    
                    <PatientHeaderToolTipItem>
                        <template #title>
                            Fax
                        </template>
                        <Phone :phone="basicInfo.referringEntityFax" />
                    </PatientHeaderToolTipItem>           
                </template>
        
                <template v-if="basicInfo.practicePhysicianName">
                    <PatientHeaderToolTipItem :topBorder="true">
                        <template #title>
                            Doctor
                        </template>
                        {{ basicInfo.practicePhysicianName}}
                    </PatientHeaderToolTipItem>            
        
                    <PatientHeaderToolTipItem>
                        <template #title>
                            Phone
                        </template>
                        <Phone :phone="basicInfo.practicePhysicianPhone" />
                    </PatientHeaderToolTipItem>
                    
                    <PatientHeaderToolTipItem>
                        <template #title>
                            Fax
                        </template>
                        {{ basicInfo.practicePhysicianFax}}
                    </PatientHeaderToolTipItem>           
                </template>
        
                <template v-if="basicInfo.primaryCarePhysicianName">
                    <PatientHeaderToolTipItem :topBorder="true">
                        <template #title>
                            PCP
                        </template>
                        {{ basicInfo.primaryCarePhysicianName}}
                    </PatientHeaderToolTipItem>            
        
                    <PatientHeaderToolTipItem>
                        <template #title>
                            Phone
                        </template>
                        <Phone :phone="basicInfo.primaryCarePhysicianPhone" />
                    </PatientHeaderToolTipItem>
                    
                    <PatientHeaderToolTipItem>
                        <template #title>
                            Fax
                        </template>
                        <Phone :phone="basicInfo.primaryCarePhysicianFax" />
                    </PatientHeaderToolTipItem>           
        
                </template>
                
                <PatientHeaderToolTipItem v-if="basicInfo.patientRoundTooltip" :topBorder="true">
                    <template #title>
                        Rounds
                    </template>
                    {{ basicInfo.patientRoundTooltip }}
                </PatientHeaderToolTipItem>
        
                <PatientHeaderToolTipItem v-if="basicInfo.patientRoundDialysisTooltip" :topBorder="!basicInfo.patientRoundTooltip">
                    <template #title>
                        Dialysis Rounds
                    </template>
                    {{ basicInfo.patientRoundDialysisTooltip }}
                </PatientHeaderToolTipItem>                                
        
                <PatientHeaderToolTipItem :topBorder="!basicInfo.patientRoundTooltip && !basicInfo.patientRoundDialysisTooltip">
                    <template #title>
                        MML Status
                    </template>
                    <XSpan @click="onClickMML" class="pointer isalus-link-action">
                        <MMLIcon :connectionType="basicInfo.mmlConnectionType" /> {{ mmlConnectionDesc(basicInfo.mmlConnectionType) }}
                    </XSpan>
                </PatientHeaderToolTipItem>

                <PatientHeaderToolTipItem v-if="showCcmStatus">
                    <template #title>
                        CCM Status
                    </template>
                    <XSpan @click="onClickMML" class="pointer isalus-link-action">
                        Enrolled on {{ basicInfo.mdrCcmDate }}
                    </XSpan>
                </PatientHeaderToolTipItem>

                <PatientHeaderToolTipItem v-if="showRpmStatus">
                    <template #title>
                        RPM Status
                    </template>
                    <XSpan @click="onClickMML" class="pointer isalus-link-action">
                        Enrolled on {{ basicInfo.mdrCcmDate }}
                    </XSpan>
                </PatientHeaderToolTipItem>            
        
                <PatientHeaderToolTipItem v-if="undefined !== basicInfo.mmlConnectionType">
                    <template #title>
                        Chart #
                    </template>
                    <XSpan @click="onClickChartNumber" class="pointer isalus-link-action" title="Click to view patient setup">
                        {{ basicInfo.patient_id }}
                    </XSpan>
                </PatientHeaderToolTipItem>

                <PatientHeaderToolTipItem :topBorder="true">
                    <template #title>
                        Flags
                    </template>
                    <XSpan @click="emit('clickEditFlagSetting')" class="pointer isalus-link-action">
                        <Icon :icon="IconTypes.Setting" /> Edit Settings
                    </XSpan>
                </PatientHeaderToolTipItem>

            </template>
        </ToolTipGridContainer>
    </div>
</template>